import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'

import {
  ALL_PARTNER_TENANTS_QUERY,
  SINGLE_PARTNER_TENANT_QUERY,
  PARTNER_TENANTS_DEPENDENTS_QUERY,
  CREATE_PARTNER_TENANT_MUTATION,
  UPDATE_PARTNER_TENANT_MUTATION,
  DELETE_PARTNER_TENANT_MUTATION,
  FORCE_DELETE_PARTNER_TENANT_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const partnerTenantRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_PARTNER_TENANT_QUERY, variables).then((data: any) => {
      return hydrateObject(data.partnerTenant)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_PARTNER_TENANTS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.partnerTenants)
    })
  },

  useObject: (id: string | number, includes = {}) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR([SINGLE_PARTNER_TENANT_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.partnerTenant),
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR([ALL_PARTNER_TENANTS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.partnerTenants),
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(PARTNER_TENANTS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([PARTNER_TENANTS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_PARTNER_TENANT_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.partnerTenant)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_PARTNER_TENANT_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.partnerTenant)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_PARTNER_TENANT_MUTATION, variables).then((data: any) => {
      return data.deletePartnerTenant
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_PARTNER_TENANT_MUTATION, variables).then((data: any) => {
      return data.forceDeletePartnerTenant
    })
  },
}

export default partnerTenantRepository
